import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromLocationCard from './reducers';
import { DeviceTypeCode } from 'app/shared/enums/device-type-code';
import { MONITOR_SERIES_TYPES } from 'app/shared/models/monitor-series-types';
import {
    BatteryVoltage,
    BlankingList,
    ChannelList,
    CommportOptions,
    CommunicationTypes,
    DataDeliveryFastMode,
    DataDeliveryFastRateList,
    DataDeliveryNormalRateList,
    DataLogModeOptions,
    DeadBandList,
    DirectionList,
    DirectionListNonTwoDirectional,
    DirectionListTwoDirectional,
    EchoInfoSensorOptions,
    EchoSensorPower,
    FlexDataDeliveryFastRateList,
    FlexDataDeliveryNormalRateList,
    FlexFlowTypeOptions,
    FlexSampleRateList,
    FlowList,
    FlowLossAlarmTypes,
    FlowLossPercentageOptions,
    FlowLossSensitivityList,
    FlowParameterOptions,
    ForeSiteNormalDeliveryList,
    ForeSiteSampleRateList,
    ForeSITEXDSensorOptions,
    GainControlOptions,
    GainValues,
    LocationStatuses,
    LocationStatusesWithNoMaintenance,
    MaintenanceIntervalHours,
    MaxVelocityMetricValuesForParaFlow,
    MaxVelocityMetricValuesNonParaFlow,
    MaxVelocityNonMetricValuesForParaFlow,
    MaxVelocityNonMetricValuesNonParaFlow,
    MetricBlankingList,
    MinVelocityMetricList,
    MinVelocityNonMetricList,
    ModeList,
    MonitoringPoints,
    NoneDynamicSection,
    NoneSensor,
    PrimarySensors,
    PVTransmitFrequencyList,
    RainInfoSensorOptions,
    SampleRateList,
    ScanRateList,
    SamplerPerWakeupOptions,
    SamplerPulseLengthOptions,
    SamplerTypes,
    SecondarySensors,
    SensitivityList,
    SensorsList,
    SVDurationList,
    SVTransmitFrequencyList,
    TritonSensorPower,
    TritonSensorTemp,
    VelocityAcquisition,
    MonitorSeriesNames,
    CheckInHour12HrOptions,
    CheckInHour24HrOptions,
} from '../location-card.constants';
import { SelectorIdentifier } from './models';
import { ButtonDisableIdentifier } from './models/validator-identifier';
import { findFieldById } from './configs/base-utils';
import { BasicInfoCommonFieldIds } from './configs/common.models';
import { FieldType } from './models/field-models';
import { SelectableOption } from './models/selectables';
import { FlexMonitorConfiguration } from './configs/flex/models';
import { RegisterEntityType, RegisterEntityTypeOption } from './models/dynamic-section-models';

export type SelectorType<TResult> = (
    state?: { locationCard: fromLocationCard.LocationCardState<unknown> },
    ...args: unknown[]
) => TResult;

export const getDisabledByIdentifier = (identifier: ButtonDisableIdentifier): SelectorType<boolean> => {
    const selectorMap: Record<ButtonDisableIdentifier, SelectorType<boolean>> = {
        [ButtonDisableIdentifier.invalid]: selectFormInvalid,
        [ButtonDisableIdentifier.pristine]: selectFormPristine,
        [ButtonDisableIdentifier.invalidOrUnsaved]: selectFormInvalidOrUnsaved,
        [ButtonDisableIdentifier.invalidOrPristine]: selectFormInvalidOrPristine,
        [ButtonDisableIdentifier.activateDisabled]: selectActivateButtonDisabled,
        [ButtonDisableIdentifier.usgsDisabled]: selectUsgsSeriesSelected,
    };

    return selectorMap[identifier];
};

export const getSelectorByIdentifier = (identifier: SelectorIdentifier) => {
    // We do not know type so it's unkown (SelectableOption), can be anything and we just promise that we can map it later
    // TODO: Refactor in future with selectors.ts, AutocompleteFieldComponent, SelectFieldComponent
    // We want to say about type here that:
    // I want to return something that can map itself OR I want to return something that can be mapped
    const selectorMap: Record<SelectorIdentifier, SelectorType<SelectableOption[]>> = {
        // global
        [SelectorIdentifier.MonitorSeriesOptions]: selectMonitorSeriesOptions,
        [SelectorIdentifier.CommunicationTypes]: communicationTypesSelector,
        [SelectorIdentifier.LocationStatuses]: locationStatusesSelector,
        [SelectorIdentifier.LocationStatusesWithNoMaintenance]: locationStatusesWithNoMaintenanceSelector,
        [SelectorIdentifier.SampleRates]: sampleRatesSelector,
        [SelectorIdentifier.ScanRates]: scanRatesSelector,
        [SelectorIdentifier.FlexSampleRates]: flexSampleRatesSelector,
        [SelectorIdentifier.DataDeliveryNormalRate]: dataDeliveryNormalRateSelector,
        [SelectorIdentifier.DataDeliveryFastRate]: dataDeliveryFastRateSelector,
        [SelectorIdentifier.ForeSiteSampleRates]: foreSiteRatesSelector,
        [SelectorIdentifier.FlexDataDeliveryNormalRateList]: flexDataDeliveryNormalRateSelector,
        [SelectorIdentifier.FlexDataDeliveryFastRateList] : flexDataDeliveryFastRateSelector,
        [SelectorIdentifier.MaintenanceIntervals]: maintenanceIntervalsSelector,
        [SelectorIdentifier.InstallationTypes]: installationTypesSelector,
        [SelectorIdentifier.FlexInstallationTypes]: flexInstallationTypesSelector,
        [SelectorIdentifier.ModemProviders]: modemProvidersSelector,
        [SelectorIdentifier.RainGauges]: rainGaugesSelector,
        [SelectorIdentifier.QFinalEntities]: qFinalEntitiesSelector,
        [SelectorIdentifier.FlowLossAlarmTypes]: flowLossAlarmTypesSelector,
        [SelectorIdentifier.FlowLossSensitivity]: flowLossSensitivitySelector,
        [SelectorIdentifier.FlowLossPercentange]: flowLossPercentangeSelector,

        // triton info
        [SelectorIdentifier.PrimarySensors]: primarySensorsSelector,
        [SelectorIdentifier.SecondarySensors]: secondarySensorsSelector,
        [SelectorIdentifier.MonitoringPoints]: monitoringPointsSelector,
        [SelectorIdentifier.Channels]: channelsSelector,
        [SelectorIdentifier.DepthSensorPreferencesCh1]: depthSensorPreferencesCh1Selector,
        [SelectorIdentifier.VelocitySensorPreferencesCh1]: velocitySensorPreferencesCh1Selector,
        [SelectorIdentifier.DepthSensorPreferencesCh2]: depthSensorPreferencesCh2Selector,
        [SelectorIdentifier.VelocitySensorPreferencesCh2]: velocitySensorPreferencesCh2Selector,
        [SelectorIdentifier.ComPorts]: comPortsSelector,
        // AV|Gated sensor selectors
        [SelectorIdentifier.FlowTypes]: flowTypesSelector,
        [SelectorIdentifier.DeadBandList]: deadBandListSelector,
        [SelectorIdentifier.DirectionList]: directionListSelector,
        [SelectorIdentifier.DirectionListTwoDirectional]: directionListSelectorTwoDirectional,
        [SelectorIdentifier.MinVelocityList]: minVelocityListSelector,
        [SelectorIdentifier.MaxVelocity]: maxVelocitySelector,
        [SelectorIdentifier.VelocityAcquisition]: velocityAcquisitionSelector,
        // Peak Combo sensor selectors
        [SelectorIdentifier.SensitivityList]: sensitivityListSelector,
        [SelectorIdentifier.TransmitFrequencyList]: transmitFrequencyListSelector,
        [SelectorIdentifier.TemperatureSensors]: temperatureSensorsSelector,

        // Long Range Depth Triton specific selectors
        [SelectorIdentifier.SensorTemp]: sensorTempSelector,
        [SelectorIdentifier.SensorPower]: sensorPowerSelector,
        [SelectorIdentifier.EchoSensorPower]: echoSensorPowerSelector,
        [SelectorIdentifier.GainControlOptions]: gainControlOptionsSelector,
        [SelectorIdentifier.GainValues]: gainValuesSelector,

        // Smart Depth sensor selectors
        [SelectorIdentifier.ModeList]: modeListSelector,
        [SelectorIdentifier.BlankingList]: blankingListSelector,

        // Surface Combo specific selectors
        [SelectorIdentifier.SVTransmitFrequencyList]: svTransmitFrequencyListSelector,
        [SelectorIdentifier.SVDirectionList]: svDirectionListSelector,
        [SelectorIdentifier.PVDirectionList]: pvDirectionListSelector,
        [SelectorIdentifier.SVDurationList]: svDurationListSelector,
        [SelectorIdentifier.SurfaceMaxVelocity]: maxVelocityParaFlowSelector,

        // Flow section selectors
        [SelectorIdentifier.FlowParamsMp1]: flowParamsMp1Selector,
        [SelectorIdentifier.FlowParamsMp2]: flowParamsMp2Selector,

        // Data delivery selectors
        [SelectorIdentifier.FastModeOptions]: foresiteDataDeliveryFastMode,

        // Echo specific selectors
        [SelectorIdentifier.EchoInfoSensorOptions]: echoInfoSensorOptions,

        // Rain specific selectors
        [SelectorIdentifier.BatteryVoltageOptions]: batteryVoltageOptions,
        [SelectorIdentifier.RainSensorOptions]: rainSensorOptions,
        [SelectorIdentifier.DataLogModeOptions]: dataLogModeOptions,

        // Foresite XD sensor options
        [SelectorIdentifier.ForeSiteNormalDeliveryList]: foreSiteNormalDeliveryList,
        [SelectorIdentifier.ForeSiteXdSensorOptions]: foreSiteXdSensorOptions,

        // Sampler selectors
        [SelectorIdentifier.SamplerTypes]: samplerTypesSelector,
        [SelectorIdentifier.SamplerPerWakeupOptions]: samplerPerWakeupOptionsSelector,
        [SelectorIdentifier.SamplerPulseLengthOptions]: samplerPulseLengthOptionsSelector,

        [SelectorIdentifier.RainGaugeAutocomplete]: rainGaugeAutocompleteSelector,
        [SelectorIdentifier.TagKeyAutocomplete]: tagkeyAutocompleteSelector,

        // Dynamic section selectors
        [SelectorIdentifier.DynamicSections]: dynamicSectionsSelector,
        [SelectorIdentifier.DynamicSectionsMp2]: dynamicSectionsSelectorMp2,
        [SelectorIdentifier.FlexFlowType]: flexFlowTypesSelector,

        // Flex monitoring point preference selectors
        [SelectorIdentifier.Mp1DepthEntityTypes]: selectMp1DepthEntityTypes,
        [SelectorIdentifier.Mp1VelocityEntityTypes]: selectMp1VelocityEntityTypes,
        [SelectorIdentifier.Mp2DepthEntityTypes]: selectMp2DepthEntityTypes,
        [SelectorIdentifier.Mp2VelocityEntityTypes]: selectMp2VelocityEntityTypes,

        // Advanced section selectors
        [SelectorIdentifier.CheckInHourOptions]: checkInHourOptionsSelector,
    };

    return selectorMap[identifier];
};

export const selectLocationCardState = createFeatureSelector<fromLocationCard.LocationCardState<unknown>>(
    fromLocationCard.locationCardFeatureKey,
);

export const selectFormState = createSelector(selectLocationCardState, (state) => state.formState);

export const selectLocationDetails = createSelector(selectLocationCardState, (state) => state.locationDetails);

export const selectMonitorConfiguration = createSelector(selectLocationCardState, (state) => state.monitorConfiguration);

export const selectLoading = createSelector(selectLocationCardState, (state) => state.loading);

export const selectLocationCardReadOnly = createSelector(
    selectLocationCardState,
    (state) => !state.permissions.isMonitorEditor,
);

export const selectFormPristine = createSelector(selectLocationCardState, (state) => state.pristine);
export const selectFormInvalid = createSelector(selectLocationCardState, (state) => !state.valid);
export const selectFormValidity = createSelector(selectLocationCardState, (state) => state.valid);
export const selectFormInvalidOrPristine = createSelector(
    selectLocationCardState,
    (state) => !state.valid || state.pristine,
);

export const selectFormInvalidOrUnsaved = createSelector(
    selectLocationCardState,
    (state) => {
        const invalid = !state.valid;
        return invalid;
    }
);


export const selectFormValid = createSelector(
    selectLocationCardState,
    state => state.valid
);

export const selectValidButPristine = createSelector(
    selectFormValid,
    selectFormPristine,
    (valid, pristine) => valid && pristine
);


const ACTIVATE_SUPPORTED_SERIES: string[] = [
    MonitorSeriesNames.triton,
    MonitorSeriesNames.echo,
    MonitorSeriesNames.rainAlert,
    MonitorSeriesNames.ForeSITE_UL.toUpperCase(),
    MonitorSeriesNames.ForeSITE_XD.toUpperCase(),
];

const IP_COMMUNICATION_TYPE = '4';

export const selectActivateButtonDisabled = createSelector(
    selectLocationCardState,
    (state) => {
        if (!state.valid || !state.locationDetails?.length || !state.formState) {
            return true;
        }

        if (!state.permissions.isMonitorEditor) {
            return true;
        }

        const commTypeField = findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType);
        if (String(commTypeField?.value) !== IP_COMMUNICATION_TYPE) {
            return true;
        }

        const seriesField = findFieldById(state.formState, BasicInfoCommonFieldIds.Series);
        const series = String(seriesField?.value ?? '').toUpperCase();

        if (series === MonitorSeriesNames.usgs.toUpperCase()) {
            return true;
        }

        return !ACTIVATE_SUPPORTED_SERIES.includes(series);
    },
);

export const selectUsgsSeriesSelected = createSelector(
    selectLocationCardState,
    (state) => {
        if (!state.formState) {
            return false;
        }

        const seriesField = findFieldById(state.formState, BasicInfoCommonFieldIds.Series);
        return String(seriesField?.value ?? '').toUpperCase() === MonitorSeriesNames.usgs.toUpperCase();
    },
);

export const selectShowConfirmation = createSelector(
    selectLocationCardState,
    (state) => state.permissions.isMonitorEditor && state.confirmation.show,
);
export const selectConfirmationData = createSelector(selectLocationCardState, (state) => state.confirmation.data);

export const selectDialogTitle = createSelector(selectLocationCardState, (state) => {
    if (state.locationDetails && state.locationDetails.length > 0 && state.formState) {
        const locationNameField = findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName, FieldType.Text);
        if (locationNameField && locationNameField.value) {
            return locationNameField.value;
        }
        return state.locationDetails[0].locationName || 'COMMON.ADD_LOCATION_TEXT';
    }
    return 'COMMON.ADD_LOCATION_TEXT';
});
// Selector to get the whole options state
export const selectOptionsState = createSelector(
    selectLocationCardState,
    (state: fromLocationCard.LocationCardState<unknown>) => state.options,
);

// Add proper type annotation for the monitor series selector
export const selectMonitorSeriesOptions = createSelector(
    selectOptionsState,
    (options: fromLocationCard.LocationCardState<unknown>['options']) => options.monitorSeries?.data || [],
);

export const depthSensorPreferencesCh1Selector = createSelector(
    selectOptionsState,
    (options) => options.depthPreferencesFirst || [],
);
export const velocitySensorPreferencesCh1Selector = createSelector(
    selectOptionsState,
    (options) => options.velPreferencesFirst || [],
);

export const depthSensorPreferencesCh2Selector = createSelector(
    selectOptionsState,
    (options) => options.depthPreferencesSecond || [],
);
export const velocitySensorPreferencesCh2Selector = createSelector(
    selectOptionsState,
    (options) => options.velPreferencesSecond || [],
);

export const dynamicSectionsSelector = createSelector(selectOptionsState, (options) => options.dynamicSections || []);
export const dynamicSectionsSelectorMp2 = createSelector(selectOptionsState, (options) => [
    NoneDynamicSection,
    ...options.dynamicSections,
]);

// autocomplete options
export const selectAutocompleteState = createSelector(
    selectLocationCardState,
    (state: fromLocationCard.LocationCardState<unknown>) => state.autocompleteOptions,
);

export const rainGaugeAutocompleteSelector = createSelector(
    selectAutocompleteState,
    (options) => options.rainGauges || [],
);

export const tagkeyAutocompleteSelector = createSelector(selectAutocompleteState, (options) => options.tagKeys || []);

// Constant-based option selectors
export const communicationTypesSelector = () => {
    return CommunicationTypes;
};

export const locationStatusesSelector = () => LocationStatuses;
export const locationStatusesWithNoMaintenanceSelector = () => LocationStatusesWithNoMaintenance;

export const sampleRatesSelector = () => SampleRateList;

export const scanRatesSelector = () => ScanRateList;

export const flexSampleRatesSelector = () => FlexSampleRateList;

export const dataDeliveryNormalRateSelector = () => DataDeliveryNormalRateList;
export const dataDeliveryFastRateSelector = () => DataDeliveryFastRateList;

export const foreSiteRatesSelector = () => ForeSiteSampleRateList;
export const foreSiteNormalDeliveryList = () => ForeSiteNormalDeliveryList;

export const flexDataDeliveryNormalRateSelector = () => FlexDataDeliveryNormalRateList;
export const flexDataDeliveryFastRateSelector = () => FlexDataDeliveryFastRateList;

export const maintenanceIntervalsSelector = MaintenanceIntervalHours;

export const flowLossAlarmTypesSelector = () => FlowLossAlarmTypes;
export const flowLossSensitivitySelector = () => FlowLossSensitivityList;
export const flowLossPercentangeSelector = FlowLossPercentageOptions;

export const primarySensorsSelector = () => {
    const sensors = [...PrimarySensors].sort((a, b) => a.text.localeCompare(b.text));
    return [NoneSensor, ...sensors];
};

export const secondarySensorsSelector = () => {
    const sensors = [...SecondarySensors].sort((a, b) => a.text.localeCompare(b.text));
    return [NoneSensor, ...sensors];
};

export const monitoringPointsSelector = () => {
    return MonitoringPoints;
};

export const channelsSelector = () => {
    return ChannelList;
};

export const comPortsSelector = () => CommportOptions;

export const flowTypesSelector = () => {
    return FlowList;
};

export const deadBandListSelector = (state: { locationCard: fromLocationCard.LocationCardState<unknown> }) => {
    const isMetric = state.locationCard.settings.isMetric;

    return DeadBandList(isMetric);
};

export const directionListSelectorTwoDirectional = () => DirectionListTwoDirectional;

export const directionListSelector = () => DirectionListNonTwoDirectional;

export const minVelocityListSelector = (state: { locationCard: fromLocationCard.LocationCardState<unknown> }) => {
    const isMetric = state.locationCard.settings.isMetric;

    return isMetric ? MinVelocityMetricList : MinVelocityNonMetricList;
};

export const maxVelocityParaFlowSelector = (state: { locationCard: fromLocationCard.LocationCardState<unknown> }) => {
    const isMetric = state.locationCard.settings.isMetric;

    return isMetric ? MaxVelocityMetricValuesForParaFlow : MaxVelocityNonMetricValuesForParaFlow;
};

export const maxVelocitySelector = (state: { locationCard: fromLocationCard.LocationCardState<unknown> }) => {
    const isMetric = state.locationCard.settings.isMetric;

    return isMetric ? MaxVelocityMetricValuesNonParaFlow : MaxVelocityNonMetricValuesNonParaFlow;
};

export const velocityAcquisitionSelector = () => {
    return VelocityAcquisition;
};

export const sensitivityListSelector = () => {
    return SensitivityList;
};

export const transmitFrequencyListSelector = () => {
    return PVTransmitFrequencyList;
};

export const temperatureSensorsSelector = () => {
    return SensorsList;
};

export const sensorTempSelector = () => {
    return TritonSensorTemp;
};

export const sensorPowerSelector = () => {
    return TritonSensorPower(true);
};

export const echoSensorPowerSelector = () => {
    return EchoSensorPower;
};

export const gainControlOptionsSelector = () => {
    return GainControlOptions;
};

export const gainValuesSelector = () => {
    return GainValues();
};

export const modeListSelector = () => {
    return ModeList;
};

export const blankingListSelector = (state: { locationCard: fromLocationCard.LocationCardState<unknown> }) => {
    const isMetric = state.locationCard.settings.isMetric;

    return isMetric ? MetricBlankingList : BlankingList;
};

export const svTransmitFrequencyListSelector = () => {
    return SVTransmitFrequencyList;
};

export const svDirectionListSelector = () => {
    return DirectionList(true);
};

export const pvDirectionListSelector = () => {
    return DirectionList(false);
};

export const svDurationListSelector = () => {
    return SVDurationList;
};

export const foresiteDataDeliveryFastMode = () => {
    return DataDeliveryFastMode;
};

export const echoInfoSensorOptions = () => {
    return EchoInfoSensorOptions;
};

export const batteryVoltageOptions = () => {
    return BatteryVoltage;
};

export const rainSensorOptions = () => {
    return RainInfoSensorOptions;
};

export const dataLogModeOptions = () => {
    return DataLogModeOptions;
};

export const foreSiteXdSensorOptions = () => {
    return ForeSITEXDSensorOptions;
};

export const samplerTypesSelector = () => {
    return SamplerTypes;
};

export const samplerPerWakeupOptionsSelector = () => {
    return SamplerPerWakeupOptions();
};

export const samplerPulseLengthOptionsSelector = () => {
    return SamplerPulseLengthOptions();
};

export const flowParamsMp1Selector = () => {
    return FlowParameterOptions;
};

export const flowParamsMp2Selector = () => {
    return FlowParameterOptions;
};

export const checkInHourOptionsSelector = (state: { locationCard: fromLocationCard.LocationCardState<unknown> }) => {
    const is12Hour = state.locationCard.customer?.timeFormat?.toUpperCase() === 'H:MM:SS TT';
    return is12Hour ? CheckInHour12HrOptions : CheckInHour24HrOptions;
};

// API-based option selectors
export const installationTypesSelector = createSelector(
    selectOptionsState,
    (options) => options.installationTypes?.data || [],
);

export const flexInstallationTypesSelector = createSelector(
    selectOptionsState,
    (options) => (options.installationTypes?.data || []).filter((type) => type.series !== 500),
);

export const modemProvidersSelector = createSelector(
    selectOptionsState,
    (options) => options.modemProviders?.data || [],
);

export const rainGaugesSelector = createSelector(selectOptionsState, (options) => options.rainGauges?.data || []);

export const qFinalEntitiesSelector = createSelector(
    selectOptionsState,
    (options) => options.qFinalEntities?.data || [],
);

export const flexFlowTypesSelector = () => FlexFlowTypeOptions;

export const selectMp1Preferences = createSelector(
    selectMonitorConfiguration,
    (monitorConfiguration) => {
        const flexConfig = monitorConfiguration as FlexMonitorConfiguration;
        return flexConfig?.mp1Preferences ?? null;
    }
);

export const selectMp2Preferences = createSelector(
    selectMonitorConfiguration,
    (monitorConfiguration) => {
        const flexConfig = monitorConfiguration as FlexMonitorConfiguration;
        return flexConfig?.mp2Preferences ?? null;
    }
);

export function getFlexEntityTypeOptions(
    sources: readonly RegisterEntityType[] | undefined,
    channelValue: unknown,
): RegisterEntityTypeOption[] {
    if (!sources?.length) {
        return [];
    }

    const templateOptions = sources.map((source) => ({
        text: typeof channelValue === 'number' ? `${source} CH${channelValue}` : source,
        value: source,
    }));

    return [{ text: 'Auto', value: RegisterEntityType.Auto }, ...templateOptions];
}

export const selectMp1DepthEntityTypes = createSelector(
    selectOptionsState,
    selectFormState,
    (options, formState) => {
        const flexSection = formState?.config?.sections.find(s => s.id === 'flex-info-section');
        const sensorField = flexSection?.fields['flex-info-sensor1'];
        if (typeof sensorField?.value !== 'string') {
            return [];
        }

        const sensorTemplate = options.loadedDynamicConfigs?.find(config => config.id.name === sensorField.value);
        return getFlexEntityTypeOptions(
            sensorTemplate?.sourcesforDepth,
            flexSection.fields['flex-info-channel1']?.value,
        );
    }
);

export const selectMp1VelocityEntityTypes = createSelector(
    selectOptionsState,
    selectFormState,
    (options, formState) => {
        const flexSection = formState?.config?.sections.find(s => s.id === 'flex-info-section');
        const sensorField = flexSection?.fields['flex-info-sensor1'];
        if (typeof sensorField?.value !== 'string') {
            return [];
        }

        const sensorTemplate = options.loadedDynamicConfigs?.find(config => config.id.name === sensorField.value);
        return getFlexEntityTypeOptions(
            sensorTemplate?.sourcesforVelocity,
            flexSection.fields['flex-info-channel1']?.value,
        );
    }
);

export const selectMp2DepthEntityTypes = createSelector(
    selectOptionsState,
    selectFormState,
    (options, formState) => {
        const flexSection = formState?.config?.sections.find(s => s.id === 'flex-info-section');
        const sensorField = flexSection?.fields['flex-info-sensor2'];
        if (typeof sensorField?.value !== 'string' || sensorField.value === 'None') {
            return [];
        }

        const sensorTemplate = options.loadedDynamicConfigs?.find(config => config.id.name === sensorField.value);
        return getFlexEntityTypeOptions(
            sensorTemplate?.sourcesforDepth,
            flexSection.fields['flex-info-channel2']?.value,
        );
    }
);

export const selectMp2VelocityEntityTypes = createSelector(
    selectOptionsState,
    selectFormState,
    (options, formState) => {
        const flexSection = formState?.config?.sections.find(s => s.id === 'flex-info-section');
        const sensorField = flexSection?.fields['flex-info-sensor2'];
        if (typeof sensorField?.value !== 'string' || sensorField.value === 'None') {
            return [];
        }

        const sensorTemplate = options.loadedDynamicConfigs?.find(config => config.id.name === sensorField.value);
        return getFlexEntityTypeOptions(
            sensorTemplate?.sourcesforVelocity,
            flexSection.fields['flex-info-channel2']?.value,
        );
    }
);

export interface DiagnosticsInputs {
    customerId: number;
    locationId: number;
    deviceTypes: DeviceTypeCode[];
    monitorSeriesType: MONITOR_SERIES_TYPES;
}

export const selectDiagnosticsVisible = createSelector(
    selectLocationCardState,
    (state): boolean => {
        return state.diagnostics.showDiagnostics
            && !state.locationCardData?.isFromNotificationDash
            && state.permissions.isMonitorEditor
            && state.locationDetails?.length > 0;
    },
);

export const selectDiagnosticsInputs = createSelector(
    selectLocationCardState,
    (state): DiagnosticsInputs | null => {
        if (!state.locationDetails?.length || !state.locationCardData) {
            return null;
        }

        return {
            customerId: state.locationCardData.customerId,
            locationId: state.locationDetails[0].locationID,
            deviceTypes: state.diagnostics.deviceTypes,
            monitorSeriesType: state.locationDetails[0].series as MONITOR_SERIES_TYPES,
        };
    },
);
